import React, { useState, useEffect, ReactNode } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import Select from 'live/components/forms/ui/select'
import { fetchEntityOptions, fetchUnitOptions, fetchWells, WELL_API } from '../../api/api'
import { RawWellEvent, SelectOptions, Uom } from '../../utils/type'
import { CustomMeasureSelect, InformationBadge, ModalField, ModalSectionHeader, NumberInput } from '../modal-components'
import { WellErrors } from './types'
import { CustomMeasureWithTooltip } from '../modal-components-style'
import { STATIC_DATA_STATES, UnitsTypes } from '../chart-components'
import { regexNumberAndComma } from '../../utils/parse'
import { StaticDataContext } from '../../context/StaticDataContext'
import { fetchCodafWells } from '../../api/wellApi'

interface WellOptionsTemplate {
    wellType: SelectOptions[]
    wellCategory: SelectOptions[]
    referenceSystem: SelectOptions[]
}
interface WellTabProps {
    event: RawWellEvent
    children?: ReactNode
    errors: WellErrors
    wellNameOptions?: SelectOptions[]
    updateWellEvent: (event: RawWellEvent) => void
    hasPermissionToEdit: boolean
}
const WellTab: React.FC<WellTabProps> = ({
    event,
    errors,
    wellNameOptions,
    updateWellEvent,
    hasPermissionToEdit
}: WellTabProps): JSX.Element => {
    const [selectedEvent, setSelectedEvent] = useState<RawWellEvent>(event)
    const [wellTypeList, setWellTypeList] = useState<SelectOptions[]>([])
    const [wellCategoryList, setWellCategoryList] = useState<SelectOptions[]>([])
    const [wellReferenceList, setWellReferenceList] = useState<SelectOptions[]>([])
    const [waterDepthList, setWaterDepthList] = useState<Uom[]>([])
    const [defaultUnit, setDefaultUnit] = useState<Uom | null>(null)
    const [hasWellSelectedInLiveWells, setHasWellSelectedInLiveWells] = useState(true)

    const fieldDisabledAfterEntityStart = selectedEvent.state !== STATIC_DATA_STATES.EMPTY

    const handleOnChangeWell = async (option: SelectOptions) => {
        setHasWellSelectedInLiveWells(true)
        let newWellEvent = { ...selectedEvent, name: option?.label, assetId: option?.value }

        const allCodafWells = await fetchCodafWells()
        newWellEvent = allCodafWells?.find(well => well?.assetId === option?.value)

        if (newWellEvent) {
            setSelectedEvent(newWellEvent as RawWellEvent)
        } else {
            setSelectedEvent(prevState => ({
                ...prevState,
                id: null,
                name: option?.label,
                assetId: option?.value,
                waterDepth: { ...prevState.waterDepth, uom: { ...prevState.waterDepth?.uom, id: null } }
            }))
        }
    }

    useEffect(() => {
        fetchWells().then(liveWells =>
            setHasWellSelectedInLiveWells(liveWells?.some(well => well?.id === selectedEvent?.assetId))
        )
    }, [])

    // fetches
    useEffect(() => {
        fetchEntityOptions(WELL_API).then((e: WellOptionsTemplate | null) => {
            if (!e) return
            setWellTypeList(e.wellType as SelectOptions[])
            setWellCategoryList(e.wellCategory as SelectOptions[])
            setWellReferenceList(e.referenceSystem as SelectOptions[])
        })
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setWaterDepthList(r.options)
            const defaultUnit = r.options.find(elem => elem.name == 'm') || r[0]
            setDefaultUnit(defaultUnit)
            setSelectedEvent(prevstate => ({
                ...prevstate,
                waterDepth:
                    prevstate.wellType != 'ONSHORE'
                        ? {
                              value: prevstate.waterDepth?.value,
                              uom: prevstate.waterDepth?.uom || defaultUnit
                          }
                        : null
            }))
        })
    }, [])

    useEffect(() => {
        updateWellEvent(selectedEvent)
    }, [selectedEvent])

    useEffect(() => {
        if (selectedEvent.wellType === 'ONSHORE') {
            setSelectedEvent(prevstate => ({ ...prevstate, waterDepth: null }))
        }
    }, [selectedEvent.wellType])

    return (
        <>
            <div onMouseDown={e => e.stopPropagation()}>
                <ModalSectionHeader label={i18n('Attributes')} />
                <ModalField
                    label={`Name`}
                    labelAditionalField={
                        selectedEvent.state !== STATIC_DATA_STATES.EMPTY && !hasWellSelectedInLiveWells ? (
                            <InformationBadge
                                id="well-name-tooltip"
                                tooltipMessage="The selected Well has been deleted from the Live Wells list. As a result, the RPI per Well stops working, however, this does not affect the RPI per Rig, which continues to work. To solve this problem, simply select another Well in the select options."
                                bgColor="orange"
                                iconClassName="fa fa-exclamation-triangle"
                                title="Well invalid"
                            />
                        ) : (
                            <></>
                        )
                    }
                    field={
                        <Select
                            value={
                                selectedEvent?.name && {
                                    label: selectedEvent?.name,
                                    value: selectedEvent?.assetId
                                }
                            }
                            options={wellNameOptions}
                            onChange={(value, option) => {
                                handleOnChangeWell(option)
                            }}
                            clearable
                            searchable
                            disabled={!hasPermissionToEdit}
                            style={errors?.name ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    label={`Field`}
                    field={
                        <div className="input-normal">
                            <input
                                disabled={!hasPermissionToEdit}
                                style={errors?.fieldName ? { borderColor: '#D9534F' } : {}}
                                type="text"
                                value={selectedEvent.fieldName || ''}
                                onChange={e => {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({ ...prevstate, fieldName: e?.target?.value }))
                                }}
                                placeholder={i18n('Field name')}
                                id="well-field"
                            />
                        </div>
                    }
                />
                <ModalField
                    label={'Type'}
                    field={
                        <Select
                            disabled={!hasPermissionToEdit}
                            style={errors?.wellType ? { borderColor: '#D9534F' } : {}}
                            searchable={false}
                            clearable={false}
                            options={wellTypeList}
                            placeholder={
                                selectedEvent.wellType
                                    ? `${wellTypeList.find(type => type.value === selectedEvent.wellType)}`
                                    : i18n('Select...')
                            }
                            value={selectedEvent.wellType !== '' && selectedEvent.wellType}
                            onChange={value => {
                                setSelectedEvent(prevstate => ({ ...prevstate, wellType: value }))
                            }}
                            id="well-type"
                        />
                    }
                />
                <ModalField
                    label={`Water Depth`}
                    labelAditionalField={
                        <CustomMeasureWithTooltip>
                            <CustomMeasureSelect
                                // disabled={fieldDisabledAfterEntityStart}
                                disabled
                                setSelectedUomValue={uomValue =>
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        waterDepth: uomValue
                                    }))
                                }
                                selectedOptionValues={selectedEvent.waterDepth}
                                options={waterDepthList}
                            />
                        </CustomMeasureWithTooltip>
                    }
                    field={
                        <NumberInput
                            disabled={!hasPermissionToEdit || selectedEvent.wellType === 'ONSHORE'}
                            value={selectedEvent.waterDepth?.value ? selectedEvent.waterDepth.value.toString() : ''}
                            onChange={e => {
                                if (regexNumberAndComma.test(e?.target?.value)) {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        waterDepth: {
                                            value: Number(e?.target?.value),
                                            uom: selectedEvent?.waterDepth?.uom ?? defaultUnit
                                        }
                                    }))
                                }
                                if (e?.target?.value === '') {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        waterDepth: {
                                            uom: selectedEvent?.waterDepth?.uom || defaultUnit,
                                            value: null
                                        }
                                    }))
                                }
                            }}
                            style={errors?.waterDepth ? { borderColor: '#D9534F' } : {}}
                            placeholder={`0.00`}
                            id="well-waterDepth"
                        />
                    }
                />
                <ModalField
                    label={'Category'}
                    field={
                        <>
                            <Select
                                disabled={!hasPermissionToEdit}
                                style={errors?.wellCategory ? { borderColor: '#D9534F' } : {}}
                                searchable={false}
                                clearable={false}
                                options={wellCategoryList}
                                placeholder={
                                    selectedEvent.wellCategory
                                        ? `${wellCategoryList.find(cat => cat.value === selectedEvent.wellCategory)}`
                                        : i18n('Select...')
                                }
                                value={selectedEvent.wellCategory !== '' && selectedEvent.wellCategory}
                                onChange={value =>
                                    setSelectedEvent(prevstate => ({ ...prevstate, wellCategory: value }))
                                }
                                id="well-category"
                            />
                        </>
                    }
                />

                <Line />

                <ModalSectionHeader label={i18n('Position')} />
                <ModalField
                    label={'Geodetic Reference System'}
                    field={
                        <>
                            <Select
                                disabled={!hasPermissionToEdit}
                                searchable={false}
                                clearable={false}
                                options={wellReferenceList}
                                style={errors?.referenceSystem ? { borderColor: '#D9534F' } : {}}
                                placeholder={
                                    selectedEvent.referenceSystem
                                        ? `${wellReferenceList.find(
                                              ref => ref.value === selectedEvent.referenceSystem
                                          )}`
                                        : i18n('Select...')
                                }
                                value={selectedEvent.referenceSystem !== '' && selectedEvent.referenceSystem}
                                onChange={value =>
                                    setSelectedEvent(prevstate => ({ ...prevstate, referenceSystem: value }))
                                }
                                id="well-position"
                            />
                        </>
                    }
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1.6rem' }}>
                    <ModalField
                        label={'Latitude (dega)'}
                        field={
                            <div className="input-normal">
                                <NumberInput
                                    disabled={!hasPermissionToEdit}
                                    negativeNumbersAllowed
                                    value={selectedEvent.latitude ?? ''}
                                    onChange={e => {
                                        if (regexNumberAndComma.test(e?.target?.value)) {
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                latitude: Number(e?.target?.value)
                                            }))
                                        } else {
                                            if (e?.target?.value === '') {
                                                e.persist()
                                                setSelectedEvent(prevstate => ({
                                                    ...prevstate,
                                                    latitude: null
                                                }))
                                            }
                                        }
                                    }}
                                    style={errors?.latitude ? { borderColor: '#D9534F' } : {}}
                                    placeholder="-00,0000 °"
                                />
                            </div>
                        }
                    />

                    <ModalField
                        label={'Longitude (dega)'}
                        field={
                            <div className="input-normal">
                                <NumberInput
                                    disabled={!hasPermissionToEdit}
                                    negativeNumbersAllowed
                                    value={selectedEvent.longitude ?? ''}
                                    onChange={e => {
                                        if (regexNumberAndComma.test(e?.target?.value)) {
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                longitude: Number(e?.target?.value)
                                            }))
                                        }
                                        if (e?.target?.value === '') {
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                longitude: null
                                            }))
                                        }
                                    }}
                                    style={errors?.longitude ? { borderColor: '#D9534F' } : {}}
                                    placeholder="-00,0000 °"
                                />
                            </div>
                        }
                    />
                </div>
            </div>
        </>
    )
}

const Line = styled.div`
    border-bottom: 1px solid var(--gray-300);
`

export default WellTab
